import type { Customer } from '@setapart/shared';
import { clearCreatorToken, getCreatorToken, setCreatorToken } from './session';

declare const __AUTH_BASE__: string;
declare const __API_BASE__: string;

export const AUTH_BASE: string = typeof __AUTH_BASE__ === 'string' ? __AUTH_BASE__ : '';
export const API_BASE: string = typeof __API_BASE__ === 'string' ? __API_BASE__ : '';

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const t = getCreatorToken();
  const h = { ...extra };
  if (t) h['Authorization'] = `Bearer ${t}`;
  return h;
}

export async function me(): Promise<{ ok: true; customer: Customer | null }> {
  const res = await fetch(`${AUTH_BASE}/auth/me`, { credentials: 'include', headers: authHeaders() });
  if (!res.ok) return { ok: true, customer: null };
  return (await res.json()) as { ok: true; customer: Customer | null };
}

export async function requestMagicLink(email: string) {
  const res = await fetch(`${AUTH_BASE}/auth/request`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, redirect: window.location.origin + '/dashboard' }),
  });
  return (await res.json()) as { ok: boolean; error?: string; devMagicLink?: string };
}

export async function verify(token: string) {
  const res = await fetch(`${AUTH_BASE}/auth/verify`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  const body = (await res.json()) as { ok: boolean; error?: string; customer?: Customer; token?: string };
  if (body.ok && body.token) setCreatorToken(body.token);
  return { ok: body.ok, error: body.error, customer: body.customer };
}

export async function logout() {
  await fetch(`${AUTH_BASE}/auth/logout`, { method: 'POST', credentials: 'include', headers: authHeaders() });
  clearCreatorToken();
}

// ────────────────────────────────────────────────────────────────────
// Creator dashboard data

export interface CreatorSummary {
  ok: true;
  creator: {
    id: string;
    name: string;
    handle: string;
    code: string;
    commissionRate: number;
    email: string;
    active: boolean;
  };
  stripe: {
    accountId: string | null;
    status: string;
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
  };
  ytd: {
    grossCents: number;
    clawbackCents: number;
    netCents: number;
    paidCents: number;
    pendingCents: number;
    pendingClawbackCents: number;
    yearStart: number;
  };
}

export async function fetchSummary(): Promise<CreatorSummary | { ok: false; error: string }> {
  const res = await fetch(`${API_BASE}/api/creator/me/summary`, {
    credentials: 'include',
    headers: authHeaders(),
  });
  return (await res.json()) as CreatorSummary | { ok: false; error: string };
}

export async function fetchClicks(days = 30) {
  const res = await fetch(`${API_BASE}/api/creator/me/clicks?days=${days}`, {
    credentials: 'include',
    headers: authHeaders(),
  });
  return (await res.json()) as {
    ok: boolean;
    clicks?: Array<{ day: string; brand: string | null; n: number }>;
    totalCount?: number;
    since?: number;
    days?: number;
    error?: string;
  };
}

export async function fetchPayouts() {
  const res = await fetch(`${API_BASE}/api/creator/me/payouts`, {
    credentials: 'include',
    headers: authHeaders(),
  });
  return (await res.json()) as { ok: boolean; payouts?: Array<Record<string, unknown>>; error?: string };
}

export async function startConnect(creatorId: string) {
  const origin = window.location.origin;
  const res = await fetch(`${API_BASE}/api/creator/${encodeURIComponent(creatorId)}/connect/start`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      returnUrl: `${origin}/connect/stripe/return?creator_id=${encodeURIComponent(creatorId)}`,
      refreshUrl: `${origin}/connect/stripe?creator_id=${encodeURIComponent(creatorId)}`,
    }),
  });
  return (await res.json()) as { ok: boolean; url?: string; error?: string };
}
